import React, { useState } from 'react';
import { Rocket, Play, FileEdit, Users, ArrowRight } from 'lucide-react';
import { DEGAPSimulation } from '../components/DEGAPSimulation';
import { allWorkshopScenarios } from '../data/workshopScenarios';
import type { SimulationState } from '../types/workshops';

type WorkshopView = 'list' | 'simulation' | 'template' | 'role-play';

export const Workshops: React.FC = () => {
  const [currentView, setCurrentView] = useState<WorkshopView>('list');
  const [, setSelectedScenarioId] = useState<string | null>(null);

  const handleSimulationComplete = (finalScore: number, state: SimulationState) => {
    console.log('Simulation complete!', { finalScore, state });
    // TODO: Save to localStorage or backend
    alert(`Congratulations! You scored ${finalScore}%`);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedScenarioId(null);
  };

  // Workshop List View
  if (currentView === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-coral-50 border-2 border-[var(--mustard)] p-8">
          <div className="flex items-center gap-4 mb-4">
            <Rocket className="h-12 w-12 text-[var(--mustard)]" />
            <div>
              <h1 className="text-3xl font-bold text-charcoal-900">Interactive Workshops</h1>
              <p className="text-charcoal-700 mt-1">
                Practice frameworks through simulations, templates, and role-playing scenarios
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="bg-[var(--bg-raised)] p-4 border-2 border-[var(--mustard)]">
              <Play className="h-6 w-6 text-consulting-slate-blue-600 mb-2" />
              <h3 className="font-bold text-charcoal-900 mb-1">Simulations</h3>
              <p className="text-sm text-charcoal-600">
                Walk through real scenarios and see consequences of your decisions
              </p>
            </div>
            <div className="bg-[var(--bg-raised)] p-4 border-2 border-sage-200">
              <FileEdit className="h-6 w-6 text-sage-600 mb-2" />
              <h3 className="font-bold text-charcoal-900 mb-1">Templates</h3>
              <p className="text-sm text-charcoal-600">
                Fillable frameworks to plan your own initiatives
              </p>
            </div>
            <div className="bg-[var(--bg-raised)] p-4 border-2 border-coral-200">
              <Users className="h-6 w-6 text-coral-600 mb-2" />
              <h3 className="font-bold text-charcoal-900 mb-1">Role-Playing</h3>
              <p className="text-sm text-charcoal-600">
                Navigate complex stakeholder situations
              </p>
            </div>
          </div>
        </div>

        {/* Available Workshops */}
        <div>
          <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Available Workshops</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {allWorkshopScenarios.map((scenario) => (
              <div
                key={scenario.id}
                className="bg-[var(--bg-raised)] shadow-md border-2 border-consulting-slate-blue-200 hover:border-consulting-slate-blue-400 transition-all overflow-hidden"
              >
                <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 px-6 py-4 border-b-2 border-consulting-slate-blue-200">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-charcoal-900 flex-1">
                      {scenario.title}
                    </h3>
                    <div className="flex-shrink-0">
                      <Play className="h-6 w-6 text-consulting-slate-blue-600" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="px-2 py-1 bg-consulting-slate-blue-200 text-consulting-slate-blue-800 rounded font-semibold">
                      {scenario.type}
                    </span>
                    <span className="px-2 py-1 bg-sage-200 text-sage-800 rounded font-semibold">
                      {scenario.difficulty}
                    </span>
                    <span className="text-charcoal-600">⏱️ {scenario.estimatedTime} min</span>
                  </div>
                </div>

                <div className="p-6">
                  <p className="text-charcoal-700 mb-4 leading-relaxed">
                    {scenario.description}
                  </p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-charcoal-800 text-sm mb-2">You will learn:</h4>
                    <ul className="space-y-1">
                      {scenario.learningObjectives.slice(0, 3).map((objective, idx) => (
                        <li key={idx} className="text-sm text-charcoal-600 flex items-start gap-2">
                          <span className="text-sage-600 flex-shrink-0">•</span>
                          <span>{objective}</span>
                        </li>
                      ))}
                      {scenario.learningObjectives.length > 3 && (
                        <li className="text-sm text-charcoal-500 italic">
                          + {scenario.learningObjectives.length - 3} more...
                        </li>
                      )}
                    </ul>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedScenarioId(scenario.id);
                      setCurrentView(scenario.type);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white font-semibold transition-colors"
                  >
                    Start Workshop
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}

            {/* Coming Soon Cards */}
            <div className="bg-[var(--bg-raised)] shadow-md border-2 border-charcoal-200 opacity-75 overflow-hidden">
              <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 px-6 py-4 border-b-2 border-charcoal-200">
                <h3 className="text-xl font-bold text-charcoal-700">
                  Role-Playing Scenarios
                </h3>
                <div className="text-xs text-charcoal-600 mt-1">Coming Soon</div>
              </div>
              <div className="p-6">
                <p className="text-charcoal-600 mb-4">
                  Navigate complex stakeholder situations with competing priorities and limited information.
                </p>
                <button
                  disabled
                  className="w-full px-4 py-3 bg-charcoal-300 text-charcoal-500 font-semibold cursor-not-allowed"
                >
                  Coming Soon
                </button>
              </div>
            </div>

            <div className="bg-[var(--bg-raised)] shadow-md border-2 border-charcoal-200 opacity-75 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-50 to-sage-50 px-6 py-4 border-b-2 border-charcoal-200">
                <h3 className="text-xl font-bold text-charcoal-700">
                  Template Builder
                </h3>
                <div className="text-xs text-charcoal-600 mt-1">Coming Soon</div>
              </div>
              <div className="p-6">
                <p className="text-charcoal-600 mb-4">
                  Design your own workshop or initiative using fillable framework templates with auto-save.
                </p>
                <button
                  disabled
                  className="w-full px-4 py-3 bg-charcoal-300 text-charcoal-500 font-semibold cursor-not-allowed"
                >
                  Coming Soon
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Simulation View
  if (currentView === 'simulation') {
    return (
      <div className="space-y-4">
        <button
          onClick={handleBackToList}
          className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium"
        >
          ← Back to Workshops
        </button>
        <DEGAPSimulation onComplete={handleSimulationComplete} />
      </div>
    );
  }

  // Placeholder for other views
  return (
    <div className="text-center py-12">
      <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Coming Soon</h2>
      <button
        onClick={handleBackToList}
        className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium"
      >
        ← Back to Workshops
      </button>
    </div>
  );
};

